import { useSearchParams } from "react-router-dom";
import {
  type ColumnPrefs,
  movePref,
  parseCols,
  reorderPref,
  serializeCols,
  togglePref,
} from "@/utils/columnPrefs-helper";

/** What the table is: every configurable id, plus how they sit before anyone touches them. */
export interface UseColumnPrefsOptions<T extends string> {
  /** The search param that carries the arrangement, e.g. `"cols"`. */
  paramName: string;
  allIds: readonly T[];
  defaultOrder: readonly T[];
  defaultHidden: readonly T[];
}

/** The current arrangement plus the four ways to change it. Spreads straight into `applyPrefs`. */
export interface UseColumnPrefsResult<T extends string> extends ColumnPrefs<T> {
  /** Show a hidden column, or hide a shown one. */
  toggle: (id: T) => void;
  /** Nudge one column by `delta` places (−1 up, +1 down); the ends are walls. */
  move: (id: T, delta: number) => void;
  /** Drop the column at index `from` at index `to` (drag & drop). */
  reorder: (from: number, to: number) => void;
  /** Back to the defaults, which removes the param entirely. */
  reset: () => void;
}

/**
 * Keeps a table's column arrangement in the URL, and nowhere else.
 *
 * The search param is the single source of truth: there is no state mirroring it, no
 * localStorage, no user metadata. That is the whole point — a table someone arranged is a link
 * they can paste into Slack, and reloading the page cannot lose the arrangement because the
 * arrangement never lived in memory. `parseCols` reads it back defensively (see
 * `utils/columnPrefs-helper.ts`), so a hand-edited or outdated param degrades to the defaults
 * instead of an empty table.
 *
 *   const cols = useColumnPrefs({ paramName: "cols", allIds: ALL_IDS, defaultOrder, defaultHidden });
 *   const columns = applyPrefs(ALL_COLUMNS, cols);
 *
 * Every write goes through `replace: true`: dragging a column into place is one arrangement, not
 * a stack of history entries the user has to back out of one at a time. Writes copy the existing
 * params, so search, filters or anything else already in the URL survive untouched.
 */
export function useColumnPrefs<T extends string>({
  paramName,
  allIds,
  defaultOrder,
  defaultHidden,
}: UseColumnPrefsOptions<T>): UseColumnPrefsResult<T> {
  const [params, setParams] = useSearchParams();

  const defaults: ColumnPrefs<T> = { order: [...defaultOrder], hidden: [...defaultHidden] };
  const prefs = parseCols(params.get(paramName), allIds, defaults);

  function commit(next: ColumnPrefs<T>) {
    const serialized = serializeCols(next, defaults);
    const search = new URLSearchParams(params);
    // The default arrangement is the absence of the param — that is also what makes reset() a
    // one-liner below: commit the defaults and the param removes itself.
    if (serialized === null) search.delete(paramName);
    else search.set(paramName, serialized);
    setParams(search, { replace: true });
  }

  return {
    ...prefs,
    toggle: (id) => commit(togglePref(prefs, id)),
    move: (id, delta) => commit(movePref(prefs, id, delta)),
    reorder: (from, to) => commit(reorderPref(prefs, from, to)),
    reset: () => commit(defaults),
  };
}
